import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react';
import {
  CompanyDto,
  getAdminToken,
  getClientToken,
  getImpersonationInfo,
  getPortalMode,
  onUnauthorized,
  PortalMode,
  setAdminToken,
  setClientToken,
  setImpersonationSession,
  syncApi,
  tenancyApi,
  UserDto,
} from '../api';

interface AuthContextValue {
  portalMode: PortalMode;
  user: UserDto | null;
  company: CompanyDto | null;
  loadingUser: boolean;
  userError: string | null;
  hasClientToken: boolean;
  hasAdminToken: boolean;
  impersonation: { active: boolean; token: string | null; label: string | null };
  maintenanceMode: boolean;
  refreshUserSession: () => Promise<void>;
  refreshMaintenanceStatus: () => Promise<void>;
  loginClientWithToken: (token: string) => Promise<void>;
  loginAdminWithToken: (token: string) => void;
  startImpersonation: (token: string, userLabel: string) => Promise<void>;
  stopImpersonation: () => Promise<void>;
  logoutClient: () => Promise<void>;
  logoutAdmin: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue>({
  portalMode: 'all',
  user: null,
  company: null,
  loadingUser: true,
  userError: null,
  hasClientToken: false,
  hasAdminToken: false,
  impersonation: { active: false, token: null, label: null },
  maintenanceMode: false,
  refreshUserSession: async () => {},
  refreshMaintenanceStatus: async () => {},
  loginClientWithToken: async () => {},
  loginAdminWithToken: () => {},
  startImpersonation: async () => {},
  stopImpersonation: async () => {},
  logoutClient: async () => {},
  logoutAdmin: async () => {},
});

/**
 * Checks URL query parameters or hash fragments for OAuth callback tokens or
 * admin "Login As" impersonation handoffs (`?auth_token_id=...`, `?impersonate_token=...`, `#token=...`).
 */
function consumeUrlAuthTokens(): void {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  const authTokenId =
    url.searchParams.get('auth_token_id') ||
    url.searchParams.get('auth-token-id') ||
    url.searchParams.get('token');
  const impersonateToken = url.searchParams.get('impersonate_token');
  const impersonateLabel = url.searchParams.get('impersonate_user');
  const adminTokenParam = url.searchParams.get('admin_token');

  let mutated = false;
  if (impersonateToken) {
    setImpersonationSession(impersonateToken, impersonateLabel || 'Impersonated User');
    url.searchParams.delete('impersonate_token');
    url.searchParams.delete('impersonate_user');
    mutated = true;
  } else if (authTokenId) {
    if (url.pathname.startsWith('/admin') || getPortalMode() === 'admin') {
      setAdminToken(authTokenId);
    } else {
      setClientToken(authTokenId);
    }
    url.searchParams.delete('auth_token_id');
    url.searchParams.delete('auth-token-id');
    url.searchParams.delete('token');
    mutated = true;
  }

  if (adminTokenParam) {
    setAdminToken(adminTokenParam);
    url.searchParams.delete('admin_token');
    mutated = true;
  }

  if (url.hash && url.hash.includes('token=')) {
    const hashParams = new URLSearchParams(url.hash.replace(/^#/, ''));
    const hashToken = hashParams.get('token') || hashParams.get('auth_token_id');
    if (hashToken) {
      if (url.pathname.startsWith('/admin') || getPortalMode() === 'admin') {
        setAdminToken(hashToken);
      } else {
        setClientToken(hashToken);
      }
      url.hash = '';
      mutated = true;
    }
  }

  if (mutated) {
    window.history.replaceState({}, '', url.pathname + url.search + url.hash);
  }
}

// Consume OAuth callback tokens immediately at module evaluation time so
// initial route guards see the persisted token before any redirect runs.
consumeUrlAuthTokens();

export function AuthProvider({ children }: { children: ReactNode }) {
  useState(() => {
    consumeUrlAuthTokens();
    return true;
  });
  const portalMode = getPortalMode();
  const [user, setUser] = useState<UserDto | null>(null);
  const [company, setCompany] = useState<CompanyDto | null>(null);
  const [loadingUser, setLoadingUser] = useState<boolean>(true);
  const [userError, setUserError] = useState<string | null>(null);
  const [hasClientToken, setHasClientToken] = useState<boolean>(() => Boolean(getClientToken()));
  const [hasAdminToken, setHasAdminToken] = useState<boolean>(() => Boolean(getAdminToken()));
  const [impersonation, setImpersonation] = useState(() => getImpersonationInfo());
  const [maintenanceMode, setMaintenanceMode] = useState<boolean>(false);

  const refreshMaintenanceStatus = useCallback(async () => {
    try {
      const res = await syncApi.getMaintenanceStatus();
      setMaintenanceMode(Boolean(res?.maintenance_mode));
    } catch {
      // Non-fatal if backend is unreachable or requires auth
    }
  }, []);

  const refreshUserSession = useCallback(async () => {
    const currentClientToken = getClientToken();
    setHasClientToken(Boolean(currentClientToken));
    setHasAdminToken(Boolean(getAdminToken()));
    setImpersonation(getImpersonationInfo());

    if (!currentClientToken) {
      setUser(null);
      setCompany(null);
      setLoadingUser(false);
      return;
    }

    setLoadingUser(true);
    setUserError(null);
    try {
      const me = await tenancyApi.getMe();
      setUser(me);
      if (me.company_id) {
        try {
          const comp = await tenancyApi.getCompany(me.company_id);
          setCompany(comp);
        } catch {
          // Fallback to /ms-user/get-company
          try {
            const comp = await tenancyApi.getCompany();
            setCompany(comp);
          } catch {
            setCompany(null);
          }
        }
      } else {
        try {
          const comp = await tenancyApi.getCompany();
          setCompany(comp);
        } catch {
          setCompany(null);
        }
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to load current user session';
      setUserError(message);
      setUser(null);
      setCompany(null);
    } finally {
      setLoadingUser(false);
    }
  }, []);

  useEffect(() => {
    consumeUrlAuthTokens();
    void refreshMaintenanceStatus();
    void refreshUserSession();
  }, [refreshMaintenanceStatus, refreshUserSession]);

  useEffect(() => {
    return onUnauthorized((scope) => {
      if (scope === 'admin') {
        setAdminToken(null);
        setHasAdminToken(false);
      } else {
        setImpersonationSession(null);
        setClientToken(null);
        setHasClientToken(false);
        setUser(null);
        setCompany(null);
        setImpersonation(getImpersonationInfo());
      }
    });
  }, []);

  const loginClientWithToken = useCallback(
    async (token: string) => {
      setClientToken(token.trim());
      setHasClientToken(true);
      await refreshUserSession();
    },
    [refreshUserSession],
  );

  const loginAdminWithToken = useCallback((token: string) => {
    setAdminToken(token.trim());
    setHasAdminToken(true);
  }, []);

  const startImpersonation = useCallback(
    async (token: string, userLabel: string) => {
      setImpersonationSession(token, userLabel);
      setImpersonation(getImpersonationInfo());
      setHasClientToken(true);
      await refreshUserSession();
    },
    [refreshUserSession],
  );

  const stopImpersonation = useCallback(async () => {
    setImpersonationSession(null);
    setImpersonation(getImpersonationInfo());
    setHasClientToken(Boolean(getClientToken()));
    await refreshUserSession();
  }, [refreshUserSession]);

  const logoutClient = useCallback(async () => {
    try {
      await tenancyApi.userLogout();
    } catch {
      // Ignore logout network error
    }
    setImpersonationSession(null);
    setClientToken(null);
    setUser(null);
    setCompany(null);
    setHasClientToken(false);
    setImpersonation(getImpersonationInfo());
  }, []);

  const logoutAdmin = useCallback(async () => {
    try {
      await tenancyApi.adminLogout();
    } catch {
      // Ignore logout network error
    }
    setAdminToken(null);
    setHasAdminToken(false);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        portalMode,
        user,
        company,
        loadingUser,
        userError,
        hasClientToken,
        hasAdminToken,
        impersonation,
        maintenanceMode,
        refreshUserSession,
        refreshMaintenanceStatus,
        loginClientWithToken,
        loginAdminWithToken,
        startImpersonation,
        stopImpersonation,
        logoutClient,
        logoutAdmin,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
